import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  FileText, Download, Share2, Mail, MessageSquare, 
  User, Phone, Mail as MailIcon, Calculator, ChevronDown, ChevronUp,
  MapPin, Calendar, Pencil
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { jsPDF } from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrency = (value) => {
  return `₹${(value || 0).toLocaleString()}`;
};

const AgentQuotes = () => {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeTab, setActiveTab] = useState("total");
  
  // Client & Markup State
  const [clientForm, setClientForm] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    markup_per_adult: 0,
    markup_per_child: 0,
    markup_per_infant: 0,
    flight_price: 0,
    hotel_price: 0,
    visa_price: 0
  });
  
  const [finalTotal, setFinalTotal] = useState(0);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error("Error fetching quotes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update quote status
  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}/agent-update`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      fetchQuotes(); // Refresh the list
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const openQuoteDetail = (quote) => {
    setSelectedQuote(quote);
    setClientForm({
      client_name: quote.client_name || "",
      client_phone: quote.client_phone || "",
      client_email: quote.client_email || "",
      markup_per_adult: quote.markup_per_adult || 0,
      markup_per_child: quote.markup_per_child || 0,
      markup_per_infant: quote.markup_per_infant || 0,
      flight_price: quote.flight_price || 0,
      hotel_price: quote.hotel_price || 0,
      visa_price: quote.visa_price || 0
    });
    setFinalTotal(quote.final_total_amount || quote.quote_amount || 0);
    setShowDetailModal(true);
  };

  const calculateTotal = () => {
    if (!selectedQuote) return 0;
    
    const baseAdult = selectedQuote.quote_per_adult || 0;
    const baseChild = selectedQuote.quote_per_child || 0;
    const baseInfant = selectedQuote.quote_per_infant || 0;
    
    const markupAdult = parseFloat(clientForm.markup_per_adult) || 0;
    const markupChild = parseFloat(clientForm.markup_per_child) || 0;
    const markupInfant = parseFloat(clientForm.markup_per_infant) || 0;
    
    const total = (
      (baseAdult + markupAdult) * selectedQuote.pax_adults +
      (baseChild + markupChild) * selectedQuote.pax_children +
      (baseInfant + markupInfant) * (selectedQuote.pax_infants || 0)
    ) + (parseFloat(clientForm.flight_price) || 0)
      + (parseFloat(clientForm.hotel_price) || 0)
      + (parseFloat(clientForm.visa_price) || 0);
    
    setFinalTotal(total);
    return total;
  };

  useEffect(() => {
    if (showDetailModal) calculateTotal();
  }, [clientForm]);

  const handleSaveClientDetails = async () => {
    try {
      const payload = {
        ...clientForm,
        final_total_amount: finalTotal
      };
      
      await axios.put(`${API}/quotes/${selectedQuote.id}/agent-update`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Client details & markup saved!");
      fetchQuotes(); // Refresh list
    } catch (error) {
      console.error("Error saving details:", error);
      toast.error("Failed to save details");
    }
  };

  const handleBooking = async () => {
    if (!clientForm.client_name || !clientForm.client_phone) {
      toast.error("Client Name and Phone are required for booking");
      return;
    }
    
    // Save details first ensures updated data is used
    await handleSaveClientDetails();

    try {
      await axios.post(`${API}/bookings`, {
        quote_id: selectedQuote.id,
        client_name: clientForm.client_name,
        client_phone: clientForm.client_phone,
        client_email: clientForm.client_email,
        markup_per_adult: clientForm.markup_per_adult,
        markup_per_child: clientForm.markup_per_child,
        markup_per_infant: clientForm.markup_per_infant,
        final_total_amount: finalTotal
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking request sent to Admin!");
      setShowDetailModal(false);
      fetchQuotes();
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Failed to submit booking");
    }
  };

  const generatePDF = (withPrice) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(46, 125, 50); // Primary Green
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text("Happy Holidays DMC", 105, 20, { align: "center" });
    doc.setFontSize(12);
    doc.text("Travel Itinerary & Quotation", 105, 30, { align: "center" });
    
    // Client Info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text(`Destination: ${selectedQuote.destination}`, 20, 60);
    doc.setFontSize(11);
    doc.text(`Travel Date: ${formatDate(selectedQuote.travel_date)}`, 20, 70);
    doc.text(`Duration: ${selectedQuote.nights || 0} Nights`, 20, 80);
    
    if (clientForm.client_name) {
      doc.text(`Prepared for: ${clientForm.client_name}`, 20, 90);
    }

    // Itinerary
    doc.setFontSize(14);
    doc.text("Itinerary Details", 20, 110);
    doc.setFontSize(10);
    const splitText = doc.splitTextToSize(selectedQuote.itinerary || "Itinerary to be added.", 170);
    doc.text(splitText, 20, 120);
    
    let yPos = 120 + (splitText.length * 5) + 10;

    // Price
    if (withPrice) {
      doc.setFillColor(240, 240, 240);
      doc.rect(20, yPos, 170, 30, 'F');
      doc.setFontSize(16);
      doc.setTextColor(46, 125, 50);
      doc.text(`Total Package Cost: ${formatCurrency(finalTotal)}`, 105, yPos + 20, { align: "center" });
    }
    
    // Footer
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(10);
    doc.text("Terms & Conditions Apply", 105, 280, { align: "center" });
    
    doc.save(`Quote_${selectedQuote.destination}_${withPrice ? 'Price' : 'NoPrice'}.pdf`);
  };

  const shareWhatsApp = () => {
    // Calculate client pricing (base + markup)
    const baseAdult = selectedQuote.quote_per_adult || 0;
    const baseChild = selectedQuote.quote_per_child || 0;
    const baseInfant = selectedQuote.quote_per_infant || 0;
    
    const markupAdult = parseFloat(clientForm.markup_per_adult) || 0;
    const markupChild = parseFloat(clientForm.markup_per_child) || 0;
    const markupInfant = parseFloat(clientForm.markup_per_infant) || 0;

    const clientAdultPrice = baseAdult + markupAdult;
    const clientChildPrice = baseChild + markupChild;
    const clientInfantPrice = baseInfant + markupInfant;

    // Build the formatted string
    let message = "";

    // Pricing Section
    if (selectedQuote.pax_adults > 0) {
      message += `₹${clientAdultPrice.toLocaleString()} PER ADULT x ${selectedQuote.pax_adults} NO OF ADULTS\n`;
    }
    if (selectedQuote.pax_children > 0) {
      message += `₹${clientChildPrice.toLocaleString()} PER KID x ${selectedQuote.pax_children} NO OF KIDS\n`;
    }
    if (selectedQuote.pax_infants > 0) {
      message += `₹${clientInfantPrice.toLocaleString()} PER INFANT x ${selectedQuote.pax_infants} no of infant\n`;
    }
    message += `\n`;

    // Overview Section
    message += `DESTINATION: ${selectedQuote.destination || ''}\n`;
    message += `COVER CITIES: ${selectedQuote.covered_cities || ''}\n`;
    message += `NO OFF ADULTS: ${selectedQuote.pax_adults || 0}\n`;
    message += `NO OF KIDS: ${selectedQuote.pax_children || 0}\n`;
    message += `NO OF INFANT: ${selectedQuote.pax_infants || 0}\n\n`;

    message += `TRAVEL DATE: ${formatDate(selectedQuote.travel_date)}\n`;
    // Calculate nights if not present or use backend value if available
    // Assuming backend provides it or we can calc from dates
    let nights = selectedQuote.nights;
    if (!nights && selectedQuote.travel_date && selectedQuote.return_date) {
        const start = new Date(selectedQuote.travel_date);
        const end = new Date(selectedQuote.return_date);
        nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    }
    message += `NO OF NIGHTS: ${nights || 0} Nights\n\n`;

    // Hotel Section
    message += `HOTEL NAME: ${selectedQuote.hotel_name || ''}\n`;
    message += `ROOM CATEGORY NAME: ${selectedQuote.room_type || ''}\n`;
    message += `MEAL PLAN: ${selectedQuote.meal_plan ? selectedQuote.meal_plan.toUpperCase() : ''}\n`;
    message += `HOTEL CATEGORY: ${selectedQuote.hotel_category || ''}\n\n`;

    // Itinerary
    message += `DAYWISE ITINERARY\n\n${selectedQuote.itinerary || ''}\n\n`;

    // Inclusions/Exclusions
    message += `INCLUSIONS\n${selectedQuote.inclusions || ''}\n\n`;
    message += `EXCLUSIONS\n${selectedQuote.exclusions || ''}\n\n`;

    // Terms
    message += `TERMS & CONDITION, IF ANY\n`; // Placeholder or add field if needed

    // Encode for URL
    const url = `https://wa.me/${clientForm.client_phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Filter logic for tabs
  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getFilteredQuotes = () => {
    switch (activeTab) {
      case "new":
        return quotes.filter(q => isToday(q.created_at));
      case "active":
        return quotes.filter(q => ['pending', 'approved', 'quoted'].includes(q.status));
      case "followup":
        return quotes.filter(q => q.status === 'followup');
      case "proposal":
        return quotes.filter(q => q.status === 'proposal_sent' || q.status === 'approved' || q.status === 'quoted');
      case "confirmed":
        return quotes.filter(q => q.status === 'booked' || q.status === 'confirmed');
      case "lost":
        return quotes.filter(q => q.status === 'lost');
      case "cancelled":
        return quotes.filter(q => q.status === 'cancelled' || q.status === 'rejected');
      default:
        return quotes;
    }
  };

  const getTabCounts = () => ({
    total: quotes.length,
    new: quotes.filter(q => isToday(q.created_at)).length,
    active: quotes.filter(q => ['pending', 'approved', 'quoted'].includes(q.status)).length,
    followup: quotes.filter(q => q.status === 'followup').length,
    proposal: quotes.filter(q => q.status === 'proposal_sent' || q.status === 'approved' || q.status === 'quoted').length,
    confirmed: quotes.filter(q => q.status === 'booked' || q.status === 'confirmed').length,
    lost: quotes.filter(q => q.status === 'lost').length,
    cancelled: quotes.filter(q => q.status === 'cancelled' || q.status === 'rejected').length,
  });

  const filteredQuotes = getFilteredQuotes();
  const tabCounts = getTabCounts();

  const tabs = [
    { id: "total", label: "Total", count: tabCounts.total, color: "bg-gray-600" },
    { id: "new", label: "New", count: tabCounts.new, color: "bg-blue-600" },
    { id: "active", label: "Active", count: tabCounts.active, color: "bg-emerald-600" },
    { id: "followup", label: "Follow Up", count: tabCounts.followup, color: "bg-amber-600" },
    { id: "proposal", label: "Proposal Sent", count: tabCounts.proposal, color: "bg-purple-600" },
    { id: "confirmed", label: "Confirmed", count: tabCounts.confirmed, color: "bg-green-600" },
    { id: "lost", label: "Lost", count: tabCounts.lost, color: "bg-red-600" },
    { id: "cancelled", label: "Cancelled", count: tabCounts.cancelled, color: "bg-slate-600" },
  ];

  return (
    <DashboardLayout type="agent">
      <div className="space-y-6" data-testid="agent-quotes">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">My Quotes</h1>
          <p className="text-gray-500">Manage your quote requests and bookings</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                activeTab === tab.id
                  ? `${tab.color} text-white shadow-md`
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id
                  ? "bg-white/20 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
              </div>
            ) : filteredQuotes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-gray-500">No quotes found in this category</p>
                <Button className="mt-4 bg-primary-700" onClick={() => setActiveTab("total")}>View All Quotes</Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Quote #</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Destination</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Travel Date</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Client</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Admin/Employee</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Est. Cost</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredQuotes.map((quote) => (
                      <tr key={quote.id} className="hover:bg-gray-50/30">
                        <td className="px-6 py-4">
                          <Link 
                            to={`/agent/quotes/${quote.id}`}
                            className="text-emerald-600 font-mono text-sm hover:text-emerald-700 hover:underline"
                            data-testid={`quote-id-${quote.id}`}
                          >
                            #{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}
                          </Link>
                        </td>
                        <td className="px-6 py-4 text-gray-800">
                          {quote.destination}
                          {(() => {
                            let n = quote.nights;
                            if (!n && quote.travel_date && quote.return_date) {
                              const s = new Date(quote.travel_date);
                              const e = new Date(quote.return_date);
                              n = Math.max(0, Math.ceil((e - s) / 86400000));
                            }
                            return n > 0 ? <span className="block text-xs text-gray-400">{n} Nights</span> : null;
                          })()}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {formatDate(quote.travel_date)}
                        </td>
                        <td className="px-6 py-4 text-gray-700 text-sm">
                          <span className="font-medium text-gray-800">{quote.client_name || '-'}</span>
                          {(() => {
                            const a = quote.pax_adults || 0;
                            const c = quote.cwb || 0;
                            const n = quote.cnb || 0;
                            const i = quote.pax_infants || 0;
                            const total = a + c + n + i;
                            if (total === 0) return null;
                            const parts = [];
                            if (a) parts.push(`${a} A`);
                            if (c) parts.push(`${c} CWB`);
                            if (n) parts.push(`${n} CNB`);
                            if (i) parts.push(`${i} Inf`);
                            return <span className="block text-xs text-gray-400">{total} Traveller{total > 1 ? 's' : ''} · {parts.join(', ')}</span>;
                          })()}
                        </td>
                        <td className="px-6 py-4 text-gray-700 text-sm">
                          <span className="font-medium text-gray-800">{quote.assigned_user_name || 'Admin'}</span>
                          {quote.assigned_user_designation && (
                            <span className="block text-xs text-gray-400">{quote.assigned_user_designation}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {/* Show dropdown for editable statuses, badge for confirmed/lost */}
                          {quote.status === 'booked' || quote.status === 'confirmed' || quote.status === 'lost' || quote.status === 'revision_requested' ? (
                            <Badge className={
                              quote.status === 'booked' || quote.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              quote.status === 'revision_requested' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {quote.status === 'revision_requested' ? 'Revision' : quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                            </Badge>
                          ) : (
                            <Select
                              value={quote.status}
                              onValueChange={(value) => updateQuoteStatus(quote.id, value)}
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs capitalize" data-testid={`status-select-${quote.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                    Active
                                  </span>
                                </SelectItem>
                                <SelectItem value="followup">
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                    Follow Up
                                  </span>
                                </SelectItem>
                                <SelectItem value="proposal_sent">
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                    Proposal Sent
                                  </span>
                                </SelectItem>
                                <SelectItem value="cancelled">
                                  <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                                    Cancelled
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-gray-800 font-medium">
                          {quote.final_total_amount ? formatCurrency(quote.final_total_amount) : 
                           quote.quote_amount ? formatCurrency(quote.quote_amount) : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {/* Edit button: agent can edit only BEFORE admin has quoted */}
                            {(!quote.quote_amount || quote.quote_amount === 0) && !['quoted', 'approved', 'booked', 'confirmed', 'cancelled', 'rejected', 'lost'].includes(quote.status) ? (
                              <Link
                                to={`/agent/request-quote?edit=${quote.id}`}
                                data-testid={`edit-quote-link-${quote.id}`}
                              >
                                <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                              </Link>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-gray-300 border-gray-200 cursor-not-allowed"
                                disabled
                                title="Cannot edit: admin has already quoted"
                              >
                                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                              </Button>
                            )}
                            {/* Full Details only active after admin has sent quotation */}
                            {quote.quote_amount > 0 || quote.status === 'approved' || quote.status === 'quoted' || quote.status === 'booked' || quote.status === 'confirmed' ? (
                              <Link 
                                to={`/agent/quotes/${quote.id}`}
                                data-testid={`full-details-link-${quote.id}`}
                              >
                                <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                                  Full Details
                                </Button>
                              </Link>
                            ) : (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-gray-400 border-gray-200 cursor-not-allowed"
                                disabled
                                title="Waiting for quotation from admin"
                              >
                                Full Details
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quote Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white border-gray-200 text-gray-800">
            <DialogHeader>
              <DialogTitle className="flex justify-between items-center pr-8">
                <span>Quote Details #{selectedQuote?.id.slice(0,8).toUpperCase()}</span>
                {(selectedQuote?.status === 'approved' || selectedQuote?.status === 'quoted') && (() => {
                  const hasGuest = !!(clientForm.client_name?.trim() && clientForm.client_phone?.trim());
                  return (
                    <Button
                      onClick={handleBooking}
                      disabled={!hasGuest}
                      className="bg-green-600 hover:bg-green-700 text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={hasGuest ? "Confirm Booking" : "Fill Guest Name and Mobile to enable"}
                      data-testid="confirm-booking-btn"
                    >
                      Confirm Booking
                    </Button>
                  );
                })()}
              </DialogTitle>
            </DialogHeader>

            {selectedQuote && (
              <div className="space-y-6">
                {/* DMC Price Section */}
                <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                  <h3 className="text-lg font-semibold mb-4 text-emerald-700 flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    DMC PRICE
                  </h3>
                  
                  <div className="space-y-3">
                    {/* Per Adult */}
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-emerald-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">Per Adult</p>
                          <p className="text-sm text-gray-500">
                            {formatCurrency(selectedQuote.quote_per_adult || 0)} × {selectedQuote.pax_adults || 0} Adults
                          </p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-gray-800">
                        {formatCurrency((selectedQuote.quote_per_adult || 0) * (selectedQuote.pax_adults || 0))}
                      </p>
                    </div>

                    {/* CWB - Only show if cwb count > 0 */}
                    {(selectedQuote.cwb > 0 || selectedQuote.quote_per_cwb > 0) && (
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-blue-100">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-blue-600">CWB</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">Child With Bed</p>
                            <p className="text-sm text-gray-500">
                              {formatCurrency(selectedQuote.quote_per_cwb || 0)} × {selectedQuote.cwb || 0} Children
                            </p>
                          </div>
                        </div>
                        <p className="text-lg font-bold text-gray-800">
                          {formatCurrency((selectedQuote.quote_per_cwb || 0) * (selectedQuote.cwb || 0))}
                        </p>
                      </div>
                    )}

                    {/* CNB - Only show if cnb count > 0 */}
                    {(selectedQuote.cnb > 0 || selectedQuote.quote_per_cnb > 0) && (
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-amber-100">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-amber-600">CNB</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">Child No Bed</p>
                            <p className="text-sm text-gray-500">
                              {formatCurrency(selectedQuote.quote_per_cnb || 0)} × {selectedQuote.cnb || 0} Children
                            </p>
                          </div>
                        </div>
                        <p className="text-lg font-bold text-gray-800">
                          {formatCurrency((selectedQuote.quote_per_cnb || 0) * (selectedQuote.cnb || 0))}
                        </p>
                      </div>
                    )}

                    {/* Total Price */}
                    <div className="flex justify-between items-center p-4 bg-emerald-600 rounded-lg mt-4">
                      <span className="text-white font-semibold text-lg">TOTAL PRICE</span>
                      <span className="text-2xl font-bold text-white">
                        {formatCurrency(selectedQuote.quote_amount || 0)}
                      </span>
                    </div>
                  </div>

                  {/* Agent Add-ons: Flight / Hotel / Visa Price */}
                  <div className="mt-5 pt-4 border-t border-emerald-200/70">
                    <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">
                      Add-on Costs (Optional)
                      <span className="text-[10px] text-gray-500 font-normal ml-2">— Shown as inclusions in the client PDF</span>
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-gray-600">Flight Price (₹)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={clientForm.flight_price}
                          onChange={e => setClientForm({...clientForm, flight_price: e.target.value})}
                          className="bg-white border-emerald-200"
                          data-testid="agent-flight-price"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Hotel Price (₹)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={clientForm.hotel_price}
                          onChange={e => setClientForm({...clientForm, hotel_price: e.target.value})}
                          className="bg-white border-emerald-200"
                          data-testid="agent-hotel-price"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Visa Price (₹)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={clientForm.visa_price}
                          onChange={e => setClientForm({...clientForm, visa_price: e.target.value})}
                          className="bg-white border-emerald-200"
                          data-testid="agent-visa-price"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Day-wise Itinerary Section */}
                {selectedQuote.activities && selectedQuote.activities.length > 0 && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <h3 className="text-lg font-semibold mb-4 text-blue-700 flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      DAY-WISE ITINERARY
                    </h3>
                    
                    <div className="space-y-3">
                      {/* Group activities by day */}
                      {Object.entries(
                        selectedQuote.activities.reduce((acc, activity) => {
                          const day = activity.day || 1;
                          if (!acc[day]) acc[day] = [];
                          acc[day].push(activity);
                          return acc;
                        }, {})
                      ).sort(([a], [b]) => Number(a) - Number(b)).map(([day, activities]) => (
                        <div key={day} className="bg-white rounded-lg border border-blue-100 overflow-hidden">
                          <div className="bg-blue-600 px-4 py-2">
                            <span className="text-white font-semibold">DAY {day}</span>
                          </div>
                          <div className="p-4 space-y-2">
                            {activities.map((activity, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <MapPin className="w-3 h-3 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-800">{activity.title}</p>
                                  {activity.description && (
                                    <p className="text-sm text-gray-500 mt-1">{activity.description}</p>
                                  )}
                                </div>
                                {activity.billable && activity.price > 0 && (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                    {formatCurrency(activity.price)}
                                  </Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                  <Button variant="outline" className="border-gray-200 text-gray-600" onClick={() => {
                    const url = `${window.location.origin}/quote/${selectedQuote.id}`;
                    navigator.clipboard.writeText(url);
                    toast.success("Public Link Copied!");
                  }}>
                    <Share2 className="w-4 h-4 mr-2" /> Share Link
                  </Button>

                {/* Client & Markup Section */}
                <div className="p-4 bg-gray-50/50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-400" /> Client Details & Markup
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label>Client Name</Label>
                      <Input value={clientForm.client_name} onChange={e => setClientForm({...clientForm, client_name: e.target.value})} className="bg-white border-gray-200" placeholder="e.g. Mr. Sharma" />
                    </div>
                    <div>
                      <Label>Mobile Number</Label>
                      <Input value={clientForm.client_phone} onChange={e => setClientForm({...clientForm, client_phone: e.target.value})} className="bg-white border-gray-200" placeholder="+91..." />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Email (Optional)</Label>
                      <Input value={clientForm.client_email} onChange={e => setClientForm({...clientForm, client_email: e.target.value})} className="bg-white border-gray-200" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 border-t border-gray-200 pt-4">
                    <div>
                      <Label className="text-xs text-gray-500">Markup Per Adult (₹)</Label>
                      <Input type="number" value={clientForm.markup_per_adult} onChange={e => setClientForm({...clientForm, markup_per_adult: e.target.value})} className="bg-white border-gray-200" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Markup Per Child (₹)</Label>
                      <Input type="number" value={clientForm.markup_per_child} onChange={e => setClientForm({...clientForm, markup_per_child: e.target.value})} className="bg-white border-gray-200" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Markup Per Infant (₹)</Label>
                      <Input type="number" value={clientForm.markup_per_infant} onChange={e => setClientForm({...clientForm, markup_per_infant: e.target.value})} className="bg-white border-gray-200" />
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-4 p-3 bg-green-900/20 rounded border border-green-900/50">
                    <span className="text-green-400 font-semibold">Final Total for Client</span>
                    <span className="text-2xl font-bold text-gray-900">{formatCurrency(finalTotal)}</span>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <Button onClick={handleSaveClientDetails} variant="outline" className="border-slate-600 text-gray-600">
                      Save Details
                    </Button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Button variant="outline" className="border-gray-200 text-gray-600" onClick={() => generatePDF(true)}>
                    <Download className="w-4 h-4 mr-2" /> PDF (With Price)
                  </Button>
                  <Button variant="outline" className="border-gray-200 text-gray-600" onClick={() => generatePDF(false)}>
                    <Download className="w-4 h-4 mr-2" /> PDF (No Price)
                  </Button>
                  <Button variant="outline" className="border-gray-200 text-gray-600">
                    <FileText className="w-4 h-4 mr-2" /> Word Doc
                  </Button>
                  <Button className="bg-green-600 hover:bg-green-700 text-gray-800" onClick={shareWhatsApp}>
                    <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AgentQuotes;
